import { useState, useEffect } from "react";
import { Mail, Save, RefreshCw } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getMailSettings, saveMailSettings, type MailSenderRow, type MailSettings, type MailSignature,
} from "../../../../lib/mail";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { CopyPill, Field, FieldGroup, Pill, SaveRow, SecretPill, SelectPill, ToggleRow } from "../panel/fields";
import { smtpSecureForPort, smtpTlsHint } from "./smtpPort";
import { SignatureListField } from "./SignatureListField";
import { SenderSettings } from "./SenderSettings";

/** Settings -> Integrations: mail transport (SMTP/Resend) + reading (IMAP/webhook). */
export function MailIntegrationCard() {
  const t = useT();
  const [s, setS] = useState<MailSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // ── Form state ──────────────────────────────────────────────────────────────
  const [senders, setSenders] = useState<MailSenderRow[]>([]);
  const [defaultSenderId, setDefaultSenderId] = useState("");
  const [outboundMode, setOutboundMode] = useState<"smtp" | "resend">("smtp");
  const [smtp, setSmtp] = useState({ host: "", port: 587, secure: false, user: "", pass: "" });
  const [resendApiKey, setResendApiKey] = useState("");
  const [imap, setImap] = useState({ host: "", port: 993, secure: true, user: "", pass: "" });
  const [inbound, setInbound] = useState({ enabled: false, pollSeconds: 120 });
  const [signatures, setSignatures] = useState<MailSignature[]>([]);
  const [defaultSignatureId, setDefaultSignatureId] = useState("");
  const [autoMatch, setAutoMatch] = useState(true);

  function hydrate(data: MailSettings) {
    setS(data);
    // Folded on the server like the signatures: a mailbox still on the single
    // address arrives as a list of one.
    setSenders(data.senders ?? []);
    setDefaultSenderId(data.defaultSenderId);
    setOutboundMode(data.outboundMode);
    setSmtp({ host: data.smtp.host, port: data.smtp.port, secure: data.smtp.secure, user: data.smtp.user, pass: "" });
    setResendApiKey("");
    setImap({ host: data.imap.host, port: data.imap.port, secure: data.imap.secure, user: data.imap.user, pass: "" });
    setInbound({ enabled: data.inbound.enabled, pollSeconds: data.inbound.pollSeconds });
    // The server has already folded the old single signature into this list, so
    // the page never has to know which era the mailbox is in.
    setSignatures(data.signatures);
    setDefaultSignatureId(data.defaultSignatureId);
    setAutoMatch(data.autoMatch);
  }

  useEffect(() => {
    getMailSettings().then(hydrate).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      await saveMailSettings({
        // The server writes the default row back into the single from-address
        // fields itself. A row added and left empty is dropped rather than
        // refused: it is a click nobody followed up, not an address.
        senders: senders
          .filter((r) => r.address.trim() !== "")
          .map((r) => ({
            id: r.id, address: r.address, name: r.name, active: r.active,
            smtpUser: r.smtpUser, smtpPass: r.smtpPass || undefined,
          })),
        defaultSenderId,
        outboundMode,
        smtp: { host: smtp.host, port: smtp.port, secure: smtp.secure, user: smtp.user, pass: smtp.pass || undefined },
        resendApiKey: resendApiKey || undefined,
        imap: { host: imap.host, port: imap.port, secure: imap.secure, user: imap.user, pass: imap.pass || undefined },
        inbound,
        signatures, defaultSignatureId,
        // Clearing the old single field is the migration, and it happens on the
        // first save of anything. It is safe because the list arrived carrying
        // that signature as an entry: without this, deleting every signature
        // would leave the legacy value behind and the server would fall back to
        // it, resurrecting a sign-off the operator had just removed.
        signature: "",
        autoMatch,
      });
      const updated = await getMailSettings();
      hydrate(updated);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  const configured = !!s?.outboundConfigured;

  return (
    <IntegrationCard
      icon={Mail}
      title={t("settings.mail.title")}
      desc={t("settings.mail.desc")}
      status={
        <ConnStatus
          ok={configured}
          okLabel={t("settings.mail.statusConfigured")}
          offLabel={t("settings.mail.statusNotConfigured")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-6">
        <SenderSettings
          rows={senders}
          defaultId={defaultSenderId}
          onChange={(rows, id) => {
            setSenders(rows);
            setDefaultSenderId(id);
          }}
          smtpMode={outboundMode === "smtp"}
          mailboxLogin={smtp.user}
        />

        <FieldGroup title={t("settings.mail.outbound")}>
          <Field label={t("settings.mail.outboundMode")} className="sm:max-w-[260px]">
            <SelectPill value={outboundMode} onChange={(v) => setOutboundMode(v as "smtp" | "resend")}>
              <option value="smtp">{t("settings.mail.modeSmtp")}</option>
              <option value="resend">{t("settings.mail.modeResend")}</option>
            </SelectPill>
          </Field>

          {outboundMode === "smtp" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("settings.mail.smtpHost")}>
                <Pill
                  value={smtp.host}
                  onChange={(v) => setSmtp({ ...smtp, host: v })}
                  placeholder="smtp.zone.eu"
                  autoComplete="off"
                />
              </Field>
              <div className="grid min-w-0 grid-cols-2 gap-4">
                <Field label={t("settings.mail.smtpPort")}>
                  <Pill
                    value={smtp.port}
                    onChange={(v) => {
                      // The well-known ports decide the TLS mode, so move the
                      // toggle with them rather than leaving a combination the
                      // server has to override behind the operator's back.
                      const port = Number(v);
                      setSmtp({ ...smtp, port, secure: smtpSecureForPort(port, smtp.secure) });
                    }}
                    type="number"
                    inputMode="numeric"
                  />
                </Field>
                <div className="flex min-w-0 items-center sm:pt-7">
                  <ToggleRow
                    label={t("settings.mail.smtpSecure")}
                    hint={smtpTlsHint(t, smtp.port, smtp.secure)}
                    checked={smtp.secure}
                    onChange={(v) => setSmtp({ ...smtp, secure: v })}
                  />
                </div>
              </div>
              <Field label={t("settings.mail.smtpUser")}>
                <Pill value={smtp.user} onChange={(v) => setSmtp({ ...smtp, user: v })} autoComplete="off" />
              </Field>
              <Field
                label={t("settings.mail.smtpPass")}
                hint={s?.smtp.passSet && !smtp.pass ? t("settings.mail.passwordKept") : undefined}
              >
                <SecretPill
                  value={smtp.pass}
                  onChange={(v) => setSmtp({ ...smtp, pass: v })}
                  placeholder={s?.smtp.passSet ? "••••••••" : ""}
                />
              </Field>
            </div>
          ) : (
            <Field
              label={t("settings.mail.resendApiKey")}
              hint={s?.resendApiKeySet && !resendApiKey ? t("settings.mail.passwordKept") : undefined}
              className="sm:max-w-[420px]"
            >
              <SecretPill
                value={resendApiKey}
                onChange={setResendApiKey}
                placeholder={s?.resendApiKeySet ? "••••••••" : "re_..."}
              />
            </Field>
          )}
        </FieldGroup>

        <FieldGroup title={t("settings.mail.inbound")}>
          <ToggleRow
            label={t("settings.mail.inboundEnabled")}
            checked={inbound.enabled}
            onChange={(v) => setInbound({ ...inbound, enabled: v })}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("settings.mail.imapHost")}>
              <Pill
                value={imap.host}
                onChange={(v) => setImap({ ...imap, host: v })}
                placeholder="imap.zone.eu"
                autoComplete="off"
              />
            </Field>
            <div className="grid min-w-0 grid-cols-2 gap-4">
              <Field label={t("settings.mail.imapPort")}>
                <Pill
                  value={imap.port}
                  onChange={(v) => setImap({ ...imap, port: Number(v) })}
                  type="number"
                  inputMode="numeric"
                />
              </Field>
              <div className="flex min-w-0 items-center sm:pt-7">
                <ToggleRow
                  label={t("settings.mail.imapSecure")}
                  checked={imap.secure}
                  onChange={(v) => setImap({ ...imap, secure: v })}
                />
              </div>
            </div>
            <Field label={t("settings.mail.imapUser")}>
              <Pill value={imap.user} onChange={(v) => setImap({ ...imap, user: v })} autoComplete="off" />
            </Field>
            <Field
              label={t("settings.mail.imapPass")}
              hint={s?.imap.passSet && !imap.pass ? t("settings.mail.passwordKept") : undefined}
            >
              <SecretPill
                value={imap.pass}
                onChange={(v) => setImap({ ...imap, pass: v })}
                placeholder={s?.imap.passSet ? "••••••••" : ""}
              />
            </Field>
            <Field label={t("settings.mail.pollSeconds")}>
              <Pill
                value={inbound.pollSeconds}
                onChange={(v) => setInbound({ ...inbound, pollSeconds: Number(v) })}
                type="number"
                inputMode="numeric"
                min={30}
              />
            </Field>
          </div>

          {/* Inbound webhook URL - the one value on this card that travels the
              other way, into the provider's console. */}
          <Field label={t("settings.mail.webhookUrl")} hint={t("settings.mail.webhookHint")}>
            <CopyPill
              value={s?.webhookUrl ?? ""}
              copyLabel={t("settings.mail.copy")}
              copiedLabel={t("settings.mail.copied")}
            />
          </Field>
        </FieldGroup>

        <div className="space-y-4">
          <SignatureListField
            rows={signatures}
            defaultId={defaultSignatureId}
            onChange={(rows, id) => {
              setSignatures(rows);
              setDefaultSignatureId(id);
            }}
          />
          <ToggleRow label={t("settings.mail.autoMatch")} checked={autoMatch} onChange={setAutoMatch} />
        </div>

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saveState === "saving" ? t("settings.mail.saving") : t("settings.mail.save")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.mail.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.mail.saveError")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
  );
}
